<template>
  <v-main style="padding:0 !important;">
    <div class="cht-root">
      <!-- ═══════════════ SIDEBAR ═══════════════ -->
      <aside class="cht-sidebar">
        <!-- Header -->
        <div class="cht-sidebar-head">
          <span class="cht-sidebar-title">
            <v-icon color="white" size="20" class="mr-1">mdi-message-text-outline</v-icon>
            Chats
          </span>
          <v-btn icon x-small color="white" @click="showNewChatDialog = true">
            <v-icon size="20">mdi-pencil-plus-outline</v-icon>
          </v-btn>
        </div>

        <!-- Search -->
        <div class="cht-search-wrap">
          <div class="cht-search-box">
            <v-icon size="16" color="#888">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              class="cht-search-input"
              placeholder="Search chats..."
            />
          </div>
        </div>

        <!-- List -->
        <div class="cht-list">
          <div
            v-for="chat in filteredChats"
            :key="chat.id"
            class="cht-item"
            :class="{ 'cht-item--active': selectedChat && selectedChat.id === chat.id }"
            @click="selectChat(chat)"
          >
            <!-- Avatar -->
            <div class="cht-av" :style="{ background: avatarColor(getChatDisplayName(chat)) }">
              <img
                v-if="chat.image_path"
                :src="`${mediaUrl}${chat.image_path}`"
                class="cht-av-img"
              />
              <span v-else class="cht-av-letters">{{ avatarInitials(getChatDisplayName(chat)) }}</span>
              <span v-if="chat.is_group" class="cht-av-group-dot">
                <v-icon size="9" color="white">mdi-account-multiple</v-icon>
              </span>
            </div>

            <!-- Info -->
            <div class="cht-item-body">
              <div class="cht-item-row1">
                <span class="cht-item-name">{{ getChatDisplayName(chat) }}</span>
                <span class="cht-item-time" v-if="chat.lastMessage">{{ formatTime(chat.lastMessage.created_at) }}</span>
              </div>
              <div class="cht-item-row2">
                <span class="cht-item-preview">
                  <v-icon v-if="chat.lastMessage && chat.lastMessage.file_path && !chat.lastMessage.message" size="12">mdi-paperclip</v-icon>
                  {{ chat.lastMessage ? (chat.lastMessage.message || 'File attachment') : 'No messages yet' }}
                </span>
                <span v-if="chat.unreadCount > 0" class="cht-badge">{{ chat.unreadCount > 99 ? '99+' : chat.unreadCount }}</span>
              </div>
            </div>
          </div>

          <div v-if="filteredChats.length === 0" class="cht-empty-list">
            <v-icon color="#ccc" size="36">mdi-chat-sleep-outline</v-icon>
            <p>No chats found</p>
          </div>
        </div>
      </aside>

      <!-- ═══════════════ CONVERSATION ═══════════════ -->
      <div class="cht-conv">
        <!-- Empty state -->
        <div v-if="!selectedChat" class="cht-conv-empty">
          <div class="cht-conv-empty-box">
            <div class="cht-conv-empty-icon">
              <v-icon size="56" color="#1976d2">mdi-forum-outline</v-icon>
            </div>
            <h3>Start a Conversation</h3>
            <p>Select a chat from the left panel to view messages.</p>
            <button class="cht-new-btn" @click="showNewChatDialog = true">
              <v-icon size="16" color="white" class="mr-1">mdi-plus</v-icon>
              New Chat
            </button>
          </div>
        </div>

        <template v-else>
          <!-- Conversation header -->
          <div class="cht-conv-head">
            <div class="cht-conv-av" :style="{ background: avatarColor(getChatDisplayName(selectedChat)) }">
              <img
                v-if="selectedChat.image_path"
                :src="`${mediaUrl}${selectedChat.image_path}`"
                class="cht-av-img"
              />
              <span v-else class="cht-av-letters" style="font-size:14px">{{ avatarInitials(getChatDisplayName(selectedChat)) }}</span>
            </div>
            <div class="cht-conv-head-info">
              <div class="cht-conv-head-name">{{ getChatDisplayName(selectedChat) }}</div>
              <div class="cht-conv-head-sub">
                {{ selectedChat.is_group ? `${selectedChat.users?.length || 0} members` : 'Direct message' }}
              </div>
            </div>
            <div class="cht-conv-head-actions">
              <v-btn icon small>
                <v-icon color="#555">mdi-magnify</v-icon>
              </v-btn>
              <v-btn icon small>
                <v-icon color="#555">mdi-dots-vertical</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Messages -->
          <div ref="messagesContainer" class="cht-msgs" @scroll="handleScroll">
            <div v-if="isLoadingMessages" class="cht-msgs-loading">
              <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
            </div>

            <template v-for="(message, index) in messages">
              <!-- Date separator -->
              <div
                v-if="shouldShowDateDivider(message, index)"
                :key="`d-${message.id}`"
                class="cht-date-sep"
              >
                <span>{{ formatDate(message.created_at) }}</span>
              </div>

              <!-- Message bubble -->
              <div
                :key="message.id"
                class="cht-msg-row"
                :class="message.sender_id === user.id ? 'cht-msg-row--own' : 'cht-msg-row--other'"
              >
                <!-- Avatar for group other -->
                <div
                  v-if="message.sender_id !== user.id && selectedChat.is_group"
                  class="cht-msg-av"
                  :style="{ background: avatarColor(message.sender?.user_name || 'U') }"
                >
                  <img
                    v-if="message.sender && message.sender.image_path"
                    :src="`${mediaUrl}${message.sender.image_path}`"
                    class="cht-av-img"
                  />
                  <span v-else class="cht-av-letters" style="font-size:10px">{{ avatarInitials(message.sender?.user_name || 'U') }}</span>
                </div>

                <!-- Bubble -->
                <div
                  class="cht-bubble"
                  :class="message.sender_id === user.id ? 'cht-bubble--own' : 'cht-bubble--other'"
                >
                  <div
                    v-if="message.sender_id !== user.id && selectedChat.is_group"
                    class="cht-bubble-sender"
                  >{{ message.sender?.user_name || message.sender?.email }}</div>

                  <div v-if="isPatientDetails(message.message)" class="cht-bubble-text cht-patient-details">
                    <div
                      v-for="(line, idx) in patientDetailsLines(message.message)"
                      :key="idx"
                      class="cht-patient-details-row"
                    >{{ line }}</div>
                  </div>
                  <div v-else-if="message.message" class="cht-bubble-text">{{ message.message }}</div>

                  <div v-if="message.file_path" class="cht-bubble-file">
                    <v-icon size="16" class="mr-1">mdi-paperclip</v-icon>
                    <span>{{ getFileName(message.file_path) }}</span>
                    <v-btn icon x-small class="ml-1" @click="downloadFile(message.file_path)">
                      <v-icon size="13">mdi-download</v-icon>
                    </v-btn>
                  </div>

                  <div class="cht-bubble-meta">
                    <span class="cht-bubble-time">{{ formatTime(message.created_at) }}</span>
                    <v-icon
                      v-if="message.sender_id === user.id"
                      :color="message.is_seen ? '#34B7F1' : 'rgba(0,0,0,0.35)'"
                      size="13"
                      class="ml-1"
                    >{{ message.is_seen ? 'mdi-check-all' : 'mdi-check' }}</v-icon>
                  </div>
                </div>
              </div>
            </template>

            <!-- Typing -->
            <div v-if="isTyping" class="cht-msg-row cht-msg-row--other">
              <div class="cht-bubble cht-bubble--other cht-typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <!-- Input bar -->
          <div class="cht-input-bar">
            <div v-if="selectedFile" class="cht-file-chip">
              <v-icon size="13" class="mr-1">mdi-file</v-icon>
              {{ selectedFile.name }}
              <v-icon size="13" class="ml-1" style="cursor:pointer" @click="selectedFile = null">mdi-close</v-icon>
            </div>
            <div class="cht-input-row">
              <button class="cht-attach-btn" @click="$refs.fileInput.click()">
                <v-icon size="22" color="#666">mdi-paperclip</v-icon>
              </button>
              <input ref="fileInput" type="file" hidden @change="handleFileSelect" />
              <input
                v-model="newMessage"
                class="cht-text-input"
                placeholder="Type a message..."
                @keydown.enter.prevent="sendMessage"
                @input="handleTyping"
              />
              <button
                class="cht-send-btn"
                :class="{ 'cht-send-btn--active': canSendMessage }"
                :disabled="!canSendMessage"
                @click="sendMessage"
              >
                <v-icon size="20" :color="canSendMessage ? 'white' : '#aaa'">mdi-send</v-icon>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- New Chat Dialog -->
    <v-dialog v-model="showNewChatDialog" max-width="420">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="primary" size="20">mdi-message-plus-outline</v-icon>
          New Chat
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-btn block color="primary" outlined @click="$router.push('/chats/create')">
            <v-icon left>mdi-plus</v-icon>Create New Chat
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="showNewChatDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "@/axios";
import WebSocketService from "@/services/websocket";

export default {
  name: "ChatInterface",
  data() {
    return {
      chats: [],
      messages: [],
      selectedChat: null,
      newMessage: "",
      selectedFile: null,
      searchQuery: "",
      isLoadingMessages: false,
      isTyping: false,
      showNewChatDialog: false,
      wsService: WebSocketService,
      typingTimeout: null,
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    mediaUrl() {
      return this.$store.getters["settings/getMediaUrl"];
    },
    filteredChats() {
      if (!this.searchQuery) return this.chats;
      return this.chats.filter((chat) =>
        this.getChatDisplayName(chat).toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    canSendMessage() {
      return (this.newMessage.trim() || this.selectedFile) && this.selectedChat;
    },
  },
  mounted() {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    this.$nextTick(() => {
      this.initializeWebSocket();
      this.fetchChats();
      if (this.$route.params.chatId) {
        this.selectChatById(this.$route.params.chatId);
      }
    });
  },
  beforeDestroy() {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    if (this.selectedChat) {
      if (this.selectedChat.is_group) {
        this.leaveGroupChatListener(this.selectedChat.id);
      } else {
        this.leavePrivateChatListener(this.selectedChat.id);
      }
    }
    this.wsService.disconnect();
  },
  methods: {
    avatarInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/).filter(w => /^[a-zA-Z]/.test(w));
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    },
    avatarColor(name) {
      const colors = [
        "#1976d2","#388e3c","#f57c00","#7b1fa2","#0097a7",
        "#c62828","#00796b","#5d4037","#455a64","#ad1457",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++)
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      return colors[Math.abs(hash) % colors.length];
    },
    initializeWebSocket() {
      this.wsService.connect();
      this.setupUserChannelListeners();
    },
    setupUserChannelListeners() {
      if (!this.user?.id) return;
      try {
        const userChannel = this.wsService.joinUserChannel(this.user.id);
        if (userChannel) {
          this.wsService.listenForMessages(`user.${this.user.id}`, (e) => {
            this.handleNewMessage(e);
          });
          this.wsService.listenForTyping(`chat.${this.user.id}`, (data) => {
            if (data.user_id !== this.user.id) this.showTypingIndicator();
          });
        }
      } catch (error) {
        console.error("Failed to setup user channel listeners:", error);
      }
    },
    setupGroupChatListener(chatId) {
      if (!chatId) return;
      try {
        const groupChannel = this.wsService.joinChatChannel(chatId, true);
        if (groupChannel) {
          this.wsService.listenForMessages(`room.${chatId}`, (e) => {
            this.handleNewMessage(e);
          });
          this.wsService.listenForTyping(`room.${chatId}`, (data) => {
            if (data.user_id !== this.user.id) this.showTypingIndicator();
          });
        }
      } catch (error) {
        console.error("Failed to setup group chat listeners:", error);
      }
    },
    setupPrivateChatListener(chatId) {
      if (!chatId) return;
      try {
        const chatChannel = this.wsService.joinChatChannel(chatId, false);
        if (chatChannel) {
          this.wsService.listenForMessages(`chat.${chatId}`, (e) => {
            this.handleNewMessage(e);
          });
        }
      } catch (error) {
        console.error("Failed to setup private chat listeners:", error);
      }
    },
    leaveGroupChatListener(chatId) {
      if (!chatId) return;
      try { this.wsService.leaveChatChannel(chatId, true); } catch (e) { /**/ }
    },
    leavePrivateChatListener(chatId) {
      if (!chatId) return;
      try { this.wsService.leaveChatChannel(chatId, false); } catch (e) { /**/ }
    },
    async fetchChats() {
      try {
        const response = await axios.get("/chats?with=users");
        this.chats = response.data.data || [];
        for (let chat of this.chats) {
          await this.fetchChatMetadata(chat);
        }
      } catch (error) {
        console.error("Error fetching chats:", error);
      }
    },
    async fetchChatMetadata(chat) {
      try {
        const r = await axios.get(`/chat_messages?chat_id=${chat.id}&per_page=1`);
        const msgs = r.data.data?.data || [];
        chat.lastMessage = msgs[0] || null;
        const u = await axios.get(`/chat_messages?chat_id=${chat.id}&is_seen=0`);
        chat.unreadCount = u.data.count || 0;
      } catch {
        chat.lastMessage = null;
        chat.unreadCount = 0;
      }
    },
    async selectChat(chat) {
      if (this.selectedChat) {
        if (this.selectedChat.is_group) this.leaveGroupChatListener(this.selectedChat.id);
        else this.leavePrivateChatListener(this.selectedChat.id);
      }
      this.selectedChat = chat;
      this.messages = [];
      this.isLoadingMessages = true;
      try {
        if (chat.is_group) this.setupGroupChatListener(chat.id);
        else this.setupPrivateChatListener(chat.id);
        const response = await axios.get(`/chat_messages?chat_id=${chat.id}&per_page=50`);
        this.messages = (response.data.data?.data || []).reverse();
        this.removeDuplicateMessages();
        await this.markMessagesAsSeen(chat.id);
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        console.error("Error loading chat messages:", error);
      } finally {
        this.isLoadingMessages = false;
      }
    },
    async selectChatById(chatId) {
      const chat = this.chats.find((c) => c.id == chatId);
      if (chat) await this.selectChat(chat);
    },
    async sendMessage() {
      if (!this.canSendMessage) return;
      const messageData = new FormData();
      messageData.append("chat_id", this.selectedChat.id);
      messageData.append("sender_id", this.user.id);
      if (this.newMessage.trim()) messageData.append("message", this.newMessage.trim());
      if (this.selectedFile) messageData.append("file", this.selectedFile);
      try {
        await axios.post("/chat_messages", messageData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        this.newMessage = "";
        this.selectedFile = null;
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },
    handleNewMessage(message) {
      if (this.selectedChat && message.chat_id === this.selectedChat.id) {
        if (!this.messages.find((m) => m.id === message.id)) {
          this.messages.push(message);
          this.$nextTick(() => this.scrollToBottom());
        }
      }
      this.updateChatInList(message);
    },
    updateChatInList(message) {
      const chat = this.chats.find((c) => c.id === message.chat_id);
      if (chat) {
        chat.lastMessage = message;
        if (message.sender_id !== this.user.id) chat.unreadCount = (chat.unreadCount || 0) + 1;
        const index = this.chats.indexOf(chat);
        if (index > 0) { this.chats.splice(index, 1); this.chats.unshift(chat); }
      }
    },
    handleTyping() {
      if (this.selectedChat && this.wsService.isConnected()) {
        const ch = this.selectedChat.is_group
          ? `room.${this.selectedChat.id}`
          : `chat.${this.selectedChat.id}`;
        this.wsService.sendTyping(ch, {
          user_id: this.user.id,
          chat_id: this.selectedChat.id,
          user_name: this.user.user_name || this.user.email,
        });
      }
    },
    showTypingIndicator() {
      this.isTyping = true;
      if (this.typingTimeout) clearTimeout(this.typingTimeout);
      this.typingTimeout = setTimeout(() => { this.isTyping = false; }, 3000);
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 10 * 1024 * 1024) { this.$toast?.error("File size cannot exceed 10MB"); return; }
        this.selectedFile = file;
      }
    },
    downloadFile(filePath) {
      if (filePath) window.open(`${this.mediaUrl}${filePath}`, "_blank");
    },
    getFileName(filePath) {
      return filePath ? filePath.split("/").pop() : "";
    },
    getChatDisplayName(chat) {
      if (chat.name) return chat.name;
      if (chat.is_group) return "Group Chat";
      if (chat.users && chat.users.length === 2) {
        const other = chat.users.find((u) => u.id !== this.user.id);
        return other ? (other.user_name || other.email) : "Direct Chat";
      }
      return "Direct Chat";
    },
    shouldShowDateDivider(message, index) {
      if (index === 0) return true;
      return (
        new Date(message.created_at).toDateString() !==
        new Date(this.messages[index - 1].created_at).toDateString()
      );
    },
    formatDate(date) {
      const d = new Date(date);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (d.toDateString() === today.toDateString()) return "Today";
      if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    isPatientDetails(text) {
      return typeof text === "string" && /^\s*Patient Details\s*:/i.test(text.trim());
    },
    patientDetailsLines(text) {
      if (!text) return [];
      let lines = text
        .split("\n")
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length <= 1) {
        const labels = "Name|Gender|Age|DOB|Blood Group|Weight|Height|Preferred Languages";
        lines = text
          .trim()
          .split(new RegExp(`\\s*(?=(?:${labels})\\s*:)`, "i"))
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
      }
      return lines;
    },
    scrollToBottom() {
      const c = this.$refs.messagesContainer;
      if (c) c.scrollTop = c.scrollHeight;
    },
    handleScroll() {
      const c = this.$refs.messagesContainer;
      if (c && c.scrollTop === 0 && !this.isLoadingMessages) this.loadMoreMessages();
    },
    async loadMoreMessages() {
      if (!this.selectedChat || this.messages.length === 0) return;
      this.isLoadingMessages = true;
      try {
        const r = await axios.get(
          `/chat_messages?chat_id=${this.selectedChat.id}&per_page=20&before=${this.messages[0].id}`
        );
        const newMsgs = (r.data.data?.data || []).reverse();
        this.addMessagesWithoutDuplicates(newMsgs, "start");
      } catch (e) { /**/ } finally {
        this.isLoadingMessages = false;
      }
    },
    async markMessagesAsSeen(chatId) {
      try {
        await axios.post(`/chat_messages/mark_seen/${chatId}`);
        this.messages.forEach((m) => { if (m.sender_id !== this.user.id) m.is_seen = true; });
        const chat = this.chats.find((c) => c.id === chatId);
        if (chat) chat.unreadCount = 0;
      } catch (e) { /**/ }
    },
    removeDuplicateMessages() {
      const seen = new Set();
      this.messages = this.messages.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
    },
    addMessagesWithoutDuplicates(newMessages, position = "end") {
      const existing = new Set(this.messages.map((m) => m.id));
      const unique = newMessages.filter((m) => !existing.has(m.id));
      if (unique.length > 0) {
        if (position === "start") this.messages.unshift(...unique);
        else this.messages.push(...unique);
      }
      return unique.length;
    },
  },
};
</script>

<style scoped>
/* ─── Root container fills v-main exactly ─── */
.cht-root {
  display: flex;
  height: calc(100vh - 64px);
  width: 100%;
  overflow: hidden;
  background: #f0f2f5;
}

/* ═══════════ SIDEBAR ═══════════ */
.cht-sidebar {
  width: 360px;
  min-width: 280px;
  max-width: 360px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e0e0e0;
  overflow: hidden;
}

.cht-sidebar-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 56px;
  background: #1976d2;
  flex-shrink: 0;
}
.cht-sidebar-title {
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.cht-search-wrap {
  padding: 8px 12px;
  background: #f7f7f7;
  border-bottom: 1px solid #ebebeb;
  flex-shrink: 0;
}
.cht-search-box {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 5px 12px;
  gap: 6px;
}
.cht-search-input {
  border: none;
  outline: none;
  font-size: 0.85rem;
  color: #333;
  flex: 1;
  background: transparent;
}
.cht-search-input::placeholder { color: #aaa; }

/* Chat list */
.cht-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}
.cht-list::-webkit-scrollbar { width: 4px; }
.cht-list::-webkit-scrollbar-thumb { background: #ddd; border-radius: 2px; }

.cht-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid #f2f2f2;
  transition: background 0.12s;
  gap: 10px;
}
.cht-item:hover { background: #f5f7fa; }
.cht-item--active {
  background: #e3f2fd !important;
  border-left: 3px solid #1976d2;
}

/* Avatars */
.cht-av {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}
.cht-av-img { width: 100%; height: 100%; object-fit: cover; }
.cht-av-letters {
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  user-select: none;
}
.cht-av-group-dot {
  position: absolute;
  bottom: 0; right: 0;
  width: 16px; height: 16px;
  background: #1565c0;
  border-radius: 50%;
  border: 1.5px solid #fff;
  display: flex; align-items: center; justify-content: center;
}

.cht-item-body { flex: 1; min-width: 0; }
.cht-item-row1 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}
.cht-item-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
.cht-item-time {
  font-size: 0.7rem;
  color: #999;
  flex-shrink: 0;
  margin-left: 6px;
}
.cht-item-row2 { display: flex; justify-content: space-between; align-items: center; }
.cht-item-preview {
  font-size: 0.78rem;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.cht-badge {
  background: #1976d2;
  color: #fff;
  border-radius: 10px;
  padding: 1px 6px;
  font-size: 0.68rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-left: 4px;
}
.cht-empty-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: #bbb;
  font-size: 0.8rem;
}

/* ═══════════ CONVERSATION ═══════════ */
.cht-conv {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: #e5ddd5;
  /* subtle pattern */
  background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b5a99a' fill-opacity='0.12'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Empty state */
.cht-conv-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f2f5;
}
.cht-conv-empty-box {
  text-align: center;
  padding: 40px 32px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  max-width: 340px;
}
.cht-conv-empty-icon {
  width: 96px; height: 96px;
  background: #e3f2fd;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
}
.cht-conv-empty-box h3 {
  font-size: 1.15rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 8px;
}
.cht-conv-empty-box p {
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 20px;
}
.cht-new-btn {
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background 0.15s;
}
.cht-new-btn:hover { background: #1565c0; }

/* Conversation header */
.cht-conv-head {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  flex-shrink: 0;
  gap: 12px;
  z-index: 1;
}
.cht-conv-av {
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}
.cht-conv-head-info { flex: 1; min-width: 0; }
.cht-conv-head-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cht-conv-head-sub { font-size: 0.72rem; color: #888; }
.cht-conv-head-actions { display: flex; gap: 2px; }

/* Messages area */
.cht-msgs {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 48px;
  min-height: 0;
}
.cht-msgs::-webkit-scrollbar { width: 5px; }
.cht-msgs::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 3px; }
.cht-msgs-loading { display: flex; justify-content: center; padding: 16px; }

/* Date separator */
.cht-date-sep {
  display: flex;
  justify-content: center;
  margin: 12px 0 8px;
}
.cht-date-sep span {
  background: #d1e8ff;
  color: #555;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 14px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}

/* Message rows */
.cht-msg-row {
  display: flex;
  align-items: flex-end;
  margin-bottom: 3px;
  gap: 6px;
}
.cht-msg-row--own { justify-content: flex-end; }
.cht-msg-row--other { justify-content: flex-start; }

.cht-msg-av {
  width: 26px; height: 26px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  margin-bottom: 2px;
}

/* Bubbles */
.cht-bubble {
  max-width: 62%;
  padding: 6px 10px 4px 10px;
  border-radius: 8px;
  word-break: break-word;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.13);
}
.cht-bubble--own {
  background: #dcf8c6;
  border-top-right-radius: 2px;
}
.cht-bubble--other {
  background: #ffffff;
  border-top-left-radius: 2px;
}
.cht-bubble-sender {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1976d2;
  margin-bottom: 2px;
}
.cht-bubble-text {
  font-size: 0.875rem;
  color: #1a1a1a;
  line-height: 1.45;
}
.cht-patient-details-row {
  white-space: nowrap;
}
.cht-patient-details-row:first-child {
  font-weight: 600;
}
.cht-bubble-file {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  color: #555;
  margin-top: 2px;
}
.cht-bubble-meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 2px;
  gap: 2px;
}
.cht-bubble-time { font-size: 0.66rem; color: #999; }

/* Typing */
.cht-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
}
.cht-typing span {
  width: 7px; height: 7px;
  border-radius: 50%;
  background: #aaa;
  display: inline-block;
  animation: cht-bounce 1.2s infinite ease-in-out;
}
.cht-typing span:nth-child(2) { animation-delay: 0.2s; }
.cht-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes cht-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-5px); }
}

/* Input bar */
.cht-input-bar {
  background: #f0f2f5;
  padding: 8px 16px 10px;
  flex-shrink: 0;
  border-top: 1px solid #ddd;
}
.cht-file-chip {
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  color: #1976d2;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 0.78rem;
  margin-bottom: 6px;
}
.cht-input-row {
  display: flex;
  align-items: center;
  background: #ffffff;
  border-radius: 24px;
  padding: 4px 4px 4px 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  gap: 6px;
}
.cht-attach-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.cht-text-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #333;
  background: transparent;
  padding: 6px 0;
}
.cht-text-input::placeholder { color: #aaa; }
.cht-send-btn {
  width: 38px; height: 38px;
  border-radius: 50%;
  border: none;
  background: #ddd;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
}
.cht-send-btn--active { background: #1976d2; }
.cht-send-btn:hover.cht-send-btn--active { background: #1565c0; }
</style>
